import { useCallback, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

// local imports
import DashboardService from 'service/DashboardService'
import { IAllFirmList } from 'types/FirmType'
import {
  ChartItems,
  IOnCampusHealthServiceAllFirmsStatistics,
  IOnCampusHealthServiceFirmStatistics,
} from 'types/DashboardType'
import DateUtil from 'utils/DateUtil'
import { roundPercentages } from 'utils/Miscellaneous'

interface IChartState {
  colors: string[]
  items: ChartItems | undefined
  labels: string[]
  footerTitle: string
  detailTitle: string
}

const chartColors = ['#3C4D9E']

/* Reducer */
const initialState: IChartState = {
  items: undefined,
  colors: chartColors,
  labels: ['dashboard.on-campus-tabs.lactation-room'],
  footerTitle: 'dashboard.on-campus-tabs.lactation-footer-msg',
  detailTitle: 'dashboard.on-campus-tabs.lactation-detail-msg',
}

function chartReducer(state: IChartState = initialState, action: { type: string; payload: { items: ChartItems } }) {
  const { type, payload } = action
  switch (type) {
    case 'all':
      return { ...initialState, ...payload }
    default:
      return initialState
  }
}

const fetchSumOfKey = (arr: any[], property: string): number => {
  return arr.map((item: any) => item[property]).reduce((prev: any, curr: any) => prev + curr, 0)
}

const percentage = (partialValue: number, total: number) => (100 * partialValue) / total

const useGetLactationServiceChartData = (props: any) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [excelData, setExcelData] = useState<any>()
  const [{ items, colors, labels, detailTitle, footerTitle }, dispatch] = useReducer(chartReducer, initialState)

  const chooseChartDataWithType = (data: IOnCampusHealthServiceAllFirmsStatistics | undefined) => {
    if (!data) return
    const totalCount = data.totalCount
    const _totalCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.totalCount)
    const totalPercent = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.totalCount, totalCount)
    })

    const payload = {
      items: {
        doughnutItems: {
          datasets: [fetchSumOfKey(data.firmStatistics, 'totalCount')],
          total: fetchSumOfKey(data.firmStatistics, 'totalCount'),
          totalPercentage: 0,
        },
        stackedItems: {
          labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
          count: [_totalCount],
          percentage: [roundPercentages(totalPercent)],
        },
      },
    }

    dispatch({ type: 'all', payload })
  }

  /* Get Lactation Room Statistics */
  const getChartData = useCallback(() => {
    let idList = props.selectedFirms.map((item: IAllFirmList) => item.id)
    const request = {
      firmIds: idList,
      startDate: DateUtil.formatDateToString(props.startDate),
      endDate: DateUtil.formatDateToString(props.endDate),
    }
    setLoading(true)
    DashboardService.getCampusLactationServiceStatistics(request)
      .then((resp) => {
        const array = resp.data.firmStatistics.filter((f) => idList.includes(f.firmId))
        chooseChartDataWithType({ ...resp.data, firmStatistics: array })
        setExcelData(array)
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.endDate, props.startDate, props.selectedFirms])

  /* Fetch Statistics */
  useEffect(() => getChartData(), [getChartData])

  return { t, excelData, loading, items, colors, labels, detailTitle, footerTitle }
}

export default useGetLactationServiceChartData
